import { useEffect } from 'react'

// MBAI-495. The backend is one Lambda; an idle container is reaped after ~10 min
// (dev CloudWatch: 559 containers, longest idle survived 10.4 min), so a ping slower
// than that always lands on a cold container. A cold container also serves only one
// request at a time, so a burst of parallel first requests spins up one cold
// container each. warmApi() is therefore de-duplicated, and every axios request
// waits on it (waitForWarmup) so the cold start is paid once, by the wakeup.
const KEEP_WARM_INTERVAL_MS = 5 * 60 * 1000
const SKIP_PING_IF_ACTIVE_MS = 2 * 60 * 1000
const CONTAINER_STALE_AFTER_MS = 8 * 60 * 1000
const WARMUP_TIMEOUT_MS = 8000

let inflight: Promise<void> | null = null
let lastActivityAt = 0

export const noteApiActivity = (): void => {
  lastActivityAt = Date.now()
}

// Plain fetch, not axios: axios requests wait on this promise, so going through
// the same interceptors would deadlock.
export function warmApi(): Promise<void> {
  if (inflight) return inflight
  const baseUrl = String(import.meta.env.VITE_APP_API_BASE_URL ?? '').replace(/\/$/, '')
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), WARMUP_TIMEOUT_MS)
  inflight = fetch(`${baseUrl}/appservice/wakeup`, { signal: controller.signal, cache: 'no-store' })
    .then(() => noteApiActivity())
    .catch(() => undefined)
    .finally(() => {
      clearTimeout(timer)
      inflight = null
    })
  return inflight
}

export function waitForWarmup(): Promise<void> {
  const containerLikelyGone = lastActivityAt > 0 && Date.now() - lastActivityAt > CONTAINER_STALE_AFTER_MS
  if (!inflight && containerLikelyGone) return warmApi()
  return inflight ?? Promise.resolve()
}

// Mount only while a user is logged in: the interval stops on logout (unmount),
// and pings are skipped while the tab is hidden or the backend was just used.
export function useKeepWarm(): void {
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastActivityAt < SKIP_PING_IF_ACTIVE_MS) return
      void warmApi()
    }, KEEP_WARM_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void waitForWarmup()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}
